import { unix } from "dayjs"
import type { ReactNode } from "react"
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { AuthContext } from "./AuthContext"
import {
  getUserAuthCookie,
  setUserAuthCookie,
} from "./AuthCookie"
import {
  getLocalStorageBaseURL,
  setLocalStorageBaseURL,
  removeLocalStorageBaseURL,
} from "./BaseURL"
import { usePageLifecycleContext } from "src/contexts/PageLifecycleContext"
import { useRefState } from "src/hooks/utils/useRefState"
import type { UserAuthCookie } from "src/types"
import { PageLifecycle } from "src/types"

interface Props {
  children: ReactNode;
}

function getIsExpired(authCookie: UserAuthCookie | null) {
  if (!authCookie || !authCookie.expires_in) {
    return false
  }

  const expiryTime = unix(authCookie.created_at)
    .add(
      authCookie.expires_in,
      "second",
    )

  // return true if it expires before the current time
  return expiryTime.isBefore(undefined)
}

export function AuthContextProvider(props: Props) {
  const pageLifecycle = usePageLifecycleContext()

  const [
    baseURL,
    setBaseURL,
    baseURLRef,
  ] = useRefState<string | null>(
    getLocalStorageBaseURL(),
  )

  const updateBaseURL = useCallback(
    (newBaseURL: string) => {
      setBaseURL(newBaseURL)
      setLocalStorageBaseURL(newBaseURL)
    },
    [setBaseURL],
  )

  const [
    authCookie,
    setAuthCookie,
    authCookieRef,
  ] = useRefState<UserAuthCookie | null>(
    baseURL === null ? null : getUserAuthCookie(),
  )

  const [
    recalculateExpirationTrigger,
    setRecalculateExpirationTrigger,
  ] = useState(0)

  const [
    isRefreshing,
    setIsRefreshing,
    isRefreshingRef,
  ] = useRefState(false)

  // recalculate isExpired when the appState, authResponse, or recalculateExpirationTrigger change
  const isExpired = useMemo(
    () => getIsExpired(authCookie),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      recalculateExpirationTrigger,
      authCookie,
      pageLifecycle,
    ],
  )

  // this side effect relies on our swr cache changing when the user is logged
  // in and logged out
  useEffect(
    () => {
      const onStorageEvent = (ev: StorageEvent) => {
        if (ev.storageArea === localStorage) {
          const newBaseURL = getLocalStorageBaseURL()
          const newCookie = getUserAuthCookie()

          const currentBaseURL = baseURLRef.current
          const currentCookie = authCookieRef.current

          const noChanges = newBaseURL === currentBaseURL &&
            newCookie?.access_token === currentCookie?.access_token

          if (noChanges) {
            return
          }

          setBaseURL(newBaseURL)

          if (newBaseURL) {
            setAuthCookie(newCookie)
          } else {
            setAuthCookie(null)
          }
        }
      }

      addEventListener(
        "storage",
        onStorageEvent,
      )

      return () => {
        removeEventListener(
          "storage",
          onStorageEvent,
        )
      }
    },
    [
      setAuthCookie,
      setBaseURL,
      authCookieRef,
      baseURLRef,
    ],
  )

  // this useEffect schedules a callback to update expiration calculation trigger
  useEffect(
    () => {
      /*
       * setTimeout execution can only happens in Active, Passive, or Hidden states
       * we want to execute even when the page is hidden so we can keep
       * updating the tabheader with the current reading
       */
      if (!authCookie || !(
        pageLifecycle === PageLifecycle.Active ||
        pageLifecycle === PageLifecycle.Passive ||
        pageLifecycle === PageLifecycle.Hidden
      )) {
        return
      }

      // milliseconds until the access token expires
      const timeUntilExpire = unix(authCookie.created_at)
        .add(
          authCookie.expires_in ?? 0,
          "second",
        )
        .diff(
          undefined,
          "millisecond",
        )

      if (timeUntilExpire <= 0) {
        return
      }

      const timeoutId = setTimeout(
        () => {
          if (authCookieRef.current === authCookie) {
            setRecalculateExpirationTrigger((v) => v + 1)
          }
        },
        timeUntilExpire,
      )

      return () => {
        clearTimeout(timeoutId)
      }
    },
    [
      authCookie,
      setRecalculateExpirationTrigger,
      authCookieRef,
      pageLifecycle,
    ],
  )

  // this sets the auth cookie, expiration, and baseURL from which the cookie came
  const updateAuthCookie = useCallback(
    (newAuthCookie: UserAuthCookie, newBaseURL: string) => {
      setAuthCookie(newAuthCookie)
      setUserAuthCookie(newAuthCookie)
      updateBaseURL(newBaseURL)
    },
    [
      setAuthCookie,
      updateBaseURL,
    ],
  )

  // this does everything that updateAuthCookie does but doesn't change the baseURL
  const refreshAuthCookie = useCallback(
    (newAuthCookie: UserAuthCookie) => {
      setAuthCookie(newAuthCookie)
      setUserAuthCookie(newAuthCookie)
    },
    [setAuthCookie],
  )

  // this clears the auth cookie and the baseURL
  const clearAuthCookie = useCallback(
    () => {
      setAuthCookie(null)
      setUserAuthCookie(null)
      setBaseURL(null)
      removeLocalStorageBaseURL()
    },
    [
      setAuthCookie,
      setBaseURL,
    ],
  )

  const value = useMemo(
    () => ({
      authCookie,
      authCookieRef,
      baseURL,
      isAuthenticated: !!authCookie,
      isExpired,
      isRefreshing,
      isRefreshingRef,
      setIsRefreshing,
      setAuthCookie: updateAuthCookie,
      refreshAuthCookie,
      clearAuthCookie,
    }),
    [
      authCookie,
      authCookieRef,
      baseURL,
      isExpired,
      isRefreshing,
      isRefreshingRef,
      setIsRefreshing,
      updateAuthCookie,
      refreshAuthCookie,
      clearAuthCookie,
    ],
  )

  return (
    <AuthContext.Provider value={value}>
      {props.children}
    </AuthContext.Provider>
  )
}
